import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import {
  Search as SearchIcon,
  DirectionsBus as DirectionsBusIcon,
  Person as PersonIcon,
  Place as PlaceIcon,
  Route as RouteIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from "@mui/icons-material";
import formatDate from "../../administration/valuations/utils/formatDate";

import axiosInstance from "../../../api/axiosInstance";
import {
  useReferenceManifestForBulk,
  useCreateBulkManifestsFromOrders,
} from "../../../hooks/useManifest";

const STEPS = [
  "Manifiesto referencia",
  "Órdenes",
  "Previsualización",
  "Resultado",
];

const formatManifestCode = (id) => `MN-${String(id).padStart(6, "0")}`;
const formatOrderCode = (id) => `OS-${String(id).padStart(6, "0")}`;

/*const formatDate = (value) => {
  if (!value) return "-";
  return String(value).slice(0, 10);
};*/

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const BulkManifestFromOrdersDialog = ({
  open,
  handleClose,
  user,
  tipo = "internos",
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);

  const [referenceInput, setReferenceInput] = useState("");
  const [referenceManID, setReferenceManID] = useState(null);
  const [searchReferenceEnabled, setSearchReferenceEnabled] = useState(false);

  const [searchOrder, setSearchOrder] = useState("");
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);

  const [config, setConfig] = useState({
    manest: "PLANIFICADO",
    maninfbase: "",
    usarFechaOrden: true,
    manfec: "",
    manfecfin: "",
    ordsermantra: "IDA",
  });

  const [result, setResult] = useState(null);

  const {
    data: referenceManifest,
    isFetching: loadingReference,
    error: referenceError,
    refetch: refetchReference,
  } = useReferenceManifestForBulk(referenceManID, false);

  const bulkMutation = useCreateBulkManifestsFromOrders();

  const empleadosReferencia = referenceManifest?.empleados || [];
  const paradasReferencia = referenceManifest?.paradas || [];

  const conductorPrincipal = empleadosReferencia[0];

  const canUseReference =
    referenceManifest &&
    referenceManifest.proid &&
    referenceManifest.vehid &&
    empleadosReferencia.length > 0 &&
    paradasReferencia.length >= 2;

  const canGoOrders = Boolean(canUseReference);

  const canGoPreview =
    canGoOrders &&
    selectedOrders.length > 0 &&
    config.manest &&
    (config.usarFechaOrden || config.manfec);

  const canSubmit = canGoPreview && !bulkMutation.isPending;

  useEffect(() => {
    if (!open) return;

    setActiveStep(0);
    setReferenceInput("");
    setReferenceManID(null);
    setSearchReferenceEnabled(false);
    setSearchOrder("");
    setAvailableOrders([]);
    setSelectedOrders([]);
    setResult(null);

    setConfig({
      manest: "PLANIFICADO",
      maninfbase: "",
      usarFechaOrden: true,
      manfec: "",
      manfecfin: "",
      ordsermantra: "IDA",
    });
  }, [open]);

  useEffect(() => {
    if (!open || activeStep !== 1) return;

    const controller = new AbortController();

    const fetchOrders = async () => {
      try {
        setLoadingOrders(true);

        const response = await axiosInstance.get(
          "/administration/service-orders/manifest-candidates/search",
          {
            params: { tipo },
            signal: controller.signal,
            timeout: 60_000, // 1 minuto            
          }
        );

        setAvailableOrders(response.data || []);
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Error cargando órdenes candidatas:", error);
        }
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchOrders();

    return () => controller.abort();
  }, [open, activeStep, tipo]);

  const filteredAvailableOrders = useMemo(() => {
    const term = searchOrder.trim().toLowerCase();

    if (!term) return availableOrders;

    return availableOrders.filter((order) => {
      const searchableText = [
        order.ordsercabid,
        order.ordsercabnom,
        order.ordsercabest,
        order.serpronom,
        order.serprodes,
        order.pronom,
        order.ordsercabfec,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(term);
    });
  }, [availableOrders, searchOrder]);

  const updateConfig = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearchReference = async () => {
    const id = Number(referenceInput);

    if (!Number.isInteger(id) || id <= 0) {
      setReferenceManID(null);
      setSearchReferenceEnabled(false);
      return;
    }

    setSelectedOrders([]);
    setAvailableOrders([]);
    setSearchOrder("");
    setReferenceManID(id);
    setSearchReferenceEnabled(true);

    setTimeout(() => {
      refetchReference?.();
    }, 0);
  };

  const previewRows = useMemo(() => {
    if (!referenceManifest) return [];

    return selectedOrders.map((order) => {
      const fechaInicio = config.usarFechaOrden
        ? order.ordsercabfec
        : config.manfec;

      const fechaFin = config.usarFechaOrden
        ? order.ordsercabfecfin
        : config.manfecfin || config.manfec;

      return {
        ordsercabid: order.ordsercabid,
        codigoOrden: formatOrderCode(order.ordsercabid),
        nombreOrden: order.ordsercabnom,
        fechaInicio,
        fechaFin,
        maninf:
          config.maninfbase?.trim() ||
          order.ordsercabnom ||
          `Manifiesto ${formatOrderCode(order.ordsercabid)}`,
        proid: referenceManifest.proid,
        vehiculo: referenceManifest.vehmat
          ? `${referenceManifest.vehmat} ${referenceManifest.vehmod || ""}`.trim()
          : `VehID ${referenceManifest.vehid}`,
        conductor: conductorPrincipal
          ? `${conductorPrincipal.PerNom || conductorPrincipal.pernom || ""} ${conductorPrincipal.PerApe || conductorPrincipal.perape || ""
            }`.trim()
          : "-",
        origen: referenceManifest.manori,
        destino: referenceManifest.mandes,
        paradas: paradasReferencia.length,
      };
    });
  }, [selectedOrders, config, referenceManifest, conductorPrincipal, paradasReferencia]);

  const handleNext = () => {
    if (activeStep === 0 && !canGoOrders) return;
    if (activeStep === 1 && selectedOrders.length === 0) return;
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const payload = {
      ordsercabids: selectedOrders.map((o) => o.ordsercabid),
      config: {
        referenceManID: Number(referenceManifest.manid),
        manest: config.manest,
        maninfbase: config.maninfbase?.trim() || null,
        usarFechaOrden: Boolean(config.usarFechaOrden),
        manfec: config.usarFechaOrden ? null : config.manfec,
        manfecfin: config.usarFechaOrden ? null : config.manfecfin || config.manfec,
        ordsermantra: config.ordsermantra || "IDA",
        crebyid: user.EmpID,
        tipo,
      },
    };

    try {
      const response = await bulkMutation.mutateAsync(payload);

      setResult(response);
      setActiveStep(3);

      if (onSuccess) onSuccess(response);
    } catch (error) {
      setResult({
        error: true,
        message: getErrorMessage(
          error,
          "Error al crear manifiestos masivamente."
        ),
      });
      setActiveStep(3);
    }
  };

  const renderReferenceStep = () => (
    <Stack spacing={2}>
      <Alert severity="info">
        Busque un manifiesto existente. Sus datos se usarán como referencia para
        crear los nuevos manifiestos: proyecto, vehículo, conductor, origen,
        destino y paradas.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            fullWidth
            label="Código de manifiesto referencia"
            placeholder="Ejemplo: 2256"
            value={referenceInput}
            onChange={(e) => setReferenceInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearchReference();
            }}
          />

          <Button
            variant="contained"
            startIcon={
              loadingReference ? <CircularProgress size={18} /> : <SearchIcon />
            }
            onClick={handleSearchReference}
            disabled={loadingReference || !referenceInput}
            sx={{ minWidth: 160 }}
          >
            Buscar
          </Button>
        </Stack>
      </Paper>

      {referenceError && (
        <Alert severity="error">
          {getErrorMessage(
            referenceError,
            "No se pudo obtener el manifiesto de referencia."
          )}
        </Alert>
      )}

      {referenceManifest && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                {formatManifestCode(referenceManifest.manid)} -{" "}
                {referenceManifest.maninf || "Sin descripción"}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Estado: {referenceManifest.manest} | Proyecto:{" "}
                {referenceManifest.pronom || referenceManifest.proid}
              </Typography>
            </Box>

            <Divider />

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={3}>
                <Chip
                  icon={<RouteIcon />}
                  label={`Ruta: ${referenceManifest.rutid || "N/A"}`}
                  color={referenceManifest.rutid ? "primary" : "default"}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Chip
                  icon={<DirectionsBusIcon />}
                  label={
                    referenceManifest.vehmat
                      ? `Vehículo: ${referenceManifest.vehmat}`
                      : `Vehículo: ${referenceManifest.vehid || "N/A"}`
                  }
                  color={referenceManifest.vehid ? "primary" : "error"}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Chip
                  icon={<PersonIcon />}
                  label={`Conductores: ${empleadosReferencia.length}`}
                  color={empleadosReferencia.length > 0 ? "primary" : "error"}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Chip
                  icon={<PlaceIcon />}
                  label={`Paradas: ${paradasReferencia.length}`}
                  color={paradasReferencia.length >= 2 ? "primary" : "error"}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label={`Origen: ${referenceManifest.manori || "No definido"}`}
                color={referenceManifest.manori ? "success" : "error"}
              />
              <Chip
                label={`Destino: ${referenceManifest.mandes || "No definido"}`}
                color={referenceManifest.mandes ? "success" : "error"}
              />
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Parada</strong></TableCell>
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell><strong>Hora referencia</strong></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paradasReferencia.map((p) => (
                    <TableRow key={p.manparid || `${p.locid}-${p.parord}`}>
                      <TableCell>{p.parord}</TableCell>
                      <TableCell>{p.locnom}</TableCell>
                      <TableCell>{p.partip}</TableCell>
                      <TableCell>
                        {p.parhorestfmt || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {!canUseReference && (
              <Alert severity="error">
                Este manifiesto no tiene todos los datos necesarios para ser usado
                como referencia.
              </Alert>
            )}
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Estado inicial de nuevos manifiestos"
              value={config.manest}
              onChange={(e) => updateConfig("manest", e.target.value)}
            >
              <MenuItem value="CREADO">CREADO</MenuItem>
              <MenuItem value="PLANIFICADO">PLANIFICADO</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Tramo"
              value={config.ordsermantra}
              onChange={(e) => updateConfig("ordsermantra", e.target.value)}
            >
              <MenuItem value="IDA">SUBIDA</MenuItem>
              <MenuItem value="VUELTA">BAJADA</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.usarFechaOrden}
                  onChange={(e) =>
                    updateConfig("usarFechaOrden", e.target.checked)
                  }
                />
              }
              label="Usar fecha de cada OS"
            />
          </Grid>

          {!config.usarFechaOrden && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  type="date"
                  fullWidth
                  label="Fecha inicio"
                  value={config.manfec}
                  onChange={(e) => updateConfig("manfec", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  type="date"
                  fullWidth
                  label="Fecha fin"
                  value={config.manfecfin}
                  onChange={(e) => updateConfig("manfecfin", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Si se deja vacío, se usará la fecha inicio."
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descripción base opcional"
              value={config.maninfbase}
              onChange={(e) => updateConfig("maninfbase", e.target.value)}
              helperText="Si se deja vacío, se usará el nombre de cada orden de servicio."
            />
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );

  const renderOrdersStep = () => {
    const liberadas = filteredAvailableOrders.filter(
      (o) => o.ordsercabest === "LIBERADA"
    );

    const enEjecucion = filteredAvailableOrders.filter(
      (o) => o.ordsercabest === "EN_EJECUCION"
    );

    const isSelected = (orderId) =>
      selectedOrders.some((o) => Number(o.ordsercabid) === Number(orderId));

    const toggleOrder = (order) => {
      setSelectedOrders((prev) => {
        const exists = prev.some(
          (o) => Number(o.ordsercabid) === Number(order.ordsercabid)
        );

        if (exists) {
          return prev.filter(
            (o) => Number(o.ordsercabid) !== Number(order.ordsercabid)
          );
        }

        return [...prev, order];
      });
    };

    const renderOrderCard = (order) => {
      const selected = isSelected(order.ordsercabid);
      const isLiberada = order.ordsercabest === "LIBERADA";

      return (
        <Paper
          key={order.ordsercabid}
          variant="outlined"
          onClick={() => toggleOrder(order)}
          sx={{
            p: 1.5,
            borderRadius: 2,
            cursor: "pointer",
            borderColor: selected
              ? "primary.main"
              : isLiberada
                ? "success.light"
                : "info.light",
            bgcolor: selected ? "primary.50" : "background.paper",
            "&:hover": {
              bgcolor: "#f8fbff",
              borderColor: "primary.main",
            },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box pt={0.3}>
              {selected ? (
                <CheckBoxIcon color="primary" />
              ) : (
                <CheckBoxOutlineBlankIcon color="action" />
              )}
            </Box>

            <Box flex={1} minWidth={0}>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Typography fontWeight={800}>
                  {formatOrderCode(order.ordsercabid)}
                </Typography>

                <Chip
                  size="small"
                  label={order.ordsercabest}
                  color={isLiberada ? "success" : "info"}
                  variant="outlined"
                />
              </Stack>

              <Typography
                variant="body2"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {order.ordsercabnom}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Fecha: {formatDate(order.ordsercabfec)} | Servicio:{" "}
                {order.serpronom || "N/A"}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      );
    };

    return (
      <Stack spacing={2}>
        <Alert severity="info">
          Se muestran órdenes <strong>LIBERADAS</strong> y{" "}
          <strong>EN_EJECUCION</strong>. Use el filtro para ubicar órdenes por código, nombre, proyecto, servicio, estado o fecha.
        </Alert>
        <TextField
          fullWidth
          size="small"
          label="Filtrar órdenes"
          placeholder="Buscar por OS, nombre, servicio, proyecto, estado o fecha..."
          value={searchOrder}
          onChange={(e) => setSearchOrder(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
            ),
          }}
        />

        {loadingOrders && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Cargando órdenes disponibles...
            </Typography>
          </Stack>
        )}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={1}
          >
            <Box>
              <Typography fontWeight={800}>
                Órdenes seleccionadas: {selectedOrders.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Disponibles filtradas: {filteredAvailableOrders.length} de{" "}
                {availableOrders.length}
              </Typography>
            </Box>

            {selectedOrders.length > 0 && (
              <Button size="small" onClick={() => setSelectedOrders([])}>
                Limpiar selección
              </Button>
            )}
          </Stack>

          {selectedOrders.length > 0 && (
            <Box display="flex" gap={1} flexWrap="wrap" mt={1.5}>
              {selectedOrders.map((order) => (
                <Chip
                  key={order.ordsercabid}
                  label={`${formatOrderCode(order.ordsercabid)} - ${formatDate(
                    order.ordsercabfec
                  )}`}
                  color={
                    order.ordsercabest === "LIBERADA" ? "success" : "info"
                  }
                  onDelete={() => toggleOrder(order)}
                />
              ))}
            </Box>
          )}
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <Chip size="small" color="success" label="LIBERADAS" />
                <Typography variant="body2" color="text.secondary">
                  {liberadas.length} orden(es)
                </Typography>
              </Stack>

              <Stack spacing={1.2} sx={{ maxHeight: 420, overflow: "auto", pr: 0.5 }}>
                {liberadas.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No hay órdenes liberadas disponibles.
                  </Typography>
                ) : (
                  liberadas.map(renderOrderCard)
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <Chip size="small" color="info" label="EN EJECUCIÓN" />
                <Typography variant="body2" color="text.secondary">
                  {enEjecucion.length} orden(es)
                </Typography>
              </Stack>

              <Stack spacing={1.2} sx={{ maxHeight: 420, overflow: "auto", pr: 0.5 }}>
                {enEjecucion.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No hay órdenes en ejecución disponibles.
                  </Typography>
                ) : (
                  enEjecucion.map(renderOrderCard)
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    );
  };

  const renderPreviewStep = () => (
    <Stack spacing={2}>
      <Alert severity="warning">
        Se creará un manifiesto por cada OS seleccionada, copiando vehículo,
        conductor, origen, destino y paradas desde el manifiesto referencia{" "}
        <strong>{formatManifestCode(referenceManifest?.manid)}</strong>.
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>OS</strong></TableCell>
              <TableCell><strong>Descripción manifiesto</strong></TableCell>
              <TableCell><strong>Fecha inicio</strong></TableCell>
              <TableCell><strong>Fecha fin</strong></TableCell>
              <TableCell><strong>Origen</strong></TableCell>
              <TableCell><strong>Destino</strong></TableCell>
              <TableCell><strong>Vehículo</strong></TableCell>
              <TableCell><strong>Conductor</strong></TableCell>
              <TableCell><strong>Paradas</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {previewRows.map((row) => (
              <TableRow key={row.ordsercabid}>
                <TableCell>{row.codigoOrden}</TableCell>
                <TableCell>{row.maninf}</TableCell>
                <TableCell>{formatDate(row.fechaInicio)}</TableCell>
                <TableCell>{formatDate(row.fechaFin)}</TableCell>
                <TableCell>{row.origen}</TableCell>
                <TableCell>{row.destino}</TableCell>
                <TableCell>{row.vehiculo}</TableCell>
                <TableCell>{row.conductor}</TableCell>
                <TableCell>{row.paradas}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );

  const renderResultStep = () => (
    <Stack spacing={2}>
      {result?.error ? (
        <Alert severity="error">{result.message}</Alert>
      ) : (
        <Alert severity="success">
          Proceso finalizado. Creados: <strong>{result?.creados || 0}</strong>,
          ignorados: <strong>{result?.ignorados || 0}</strong>.
        </Alert>
      )}

      {result?.manifiestos?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography fontWeight={800} mb={1}>
            Manifiestos creados
          </Typography>

          <Stack direction="row" gap={1} flexWrap="wrap">
            {result.manifiestos.map((m) => (
              <Chip
                key={m.manid}
                color="success"
                label={`${formatManifestCode(m.manid)} / ${formatOrderCode(
                  m.ordsercabid
                )}`}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {result?.omitidos?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography fontWeight={800} mb={1}>
            Órdenes omitidas
          </Typography>

          {result.omitidos.map((item, index) => (
            <Alert severity="warning" sx={{ mb: 1 }} key={index}>
              {formatOrderCode(item.ordsercabid)}: {item.motivo}
            </Alert>
          ))}
        </Paper>
      )}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Crear manifiestos masivamente
        <Typography variant="body2" color="text.secondary">
          Use un manifiesto real como referencia operacional para generar nuevos
          manifiestos desde órdenes de servicio.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderReferenceStep()}
        {activeStep === 1 && renderOrdersStep()}
        {activeStep === 2 && renderPreviewStep()}
        {activeStep === 3 && renderResultStep()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {activeStep === 3 ? "Cerrar" : "Cancelar"}
        </Button>

        {activeStep > 0 && activeStep < 3 && (
          <Button onClick={handleBack}>Atrás</Button>
        )}

        {activeStep < 2 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && !canGoOrders) ||
              (activeStep === 1 && selectedOrders.length === 0)
            }
          >
            Siguiente
          </Button>
        )}

        {activeStep === 2 && (
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={
              bulkMutation.isPending ? <CircularProgress size={18} /> : null
            }
          >
            {bulkMutation.isPending ? "Creando..." : "Crear manifiestos"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkManifestFromOrdersDialog;